import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  DefaultValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DigitsInfo } from '@epicuro-next/utilities';
import { Subject, takeUntil } from 'rxjs';

import { MaxValueExceededEvent } from '../../models/max-value-exceeded-event';

type PercentageType = string | null;

@Component({
  selector: 'atx-percentage-input',
  templateUrl: './percentage-input.component.html',
  styleUrls: ['./percentage-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PercentageInputComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class PercentageInputComponent
  extends DefaultValueAccessor
  implements OnInit, OnDestroy
{
  private readonly defaultFormat = '1.0-2';

  @Input() public defaultValue: PercentageType = null;
  @Input() public placeholder = '';
  @Input('i18n-placeholder') public i18nPlaceholder: string;
  @Input() public max = 100;
  @Input() public format?: string = this.defaultFormat;
  @Input() public mayShowClearButton = true;
  @Output() public clear = new EventEmitter();
  @Output() public maxExceeded = new EventEmitter<MaxValueExceededEvent>();

  protected get hasValue(): boolean {
    return this.percentageControl.value !== null;
  }

  @ViewChild('percentageInputControl')
  private inputControlElementRef: ElementRef<HTMLInputElement>;

  protected percentageControl: FormControl<PercentageType>;

  protected get mask(): string {
    const digitsInfo = DigitsInfo.fromString(this.format ?? this.defaultFormat);
    const maxFractionDigits = digitsInfo?.maxFractionDigits ?? 2;
    let maskString = '0*';
    if (maxFractionDigits > 0) {
      maskString += `.${'9'.repeat(maxFractionDigits)}`;
    }
    return maskString;
  }

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly decimalPipe: DecimalPipe,
    readonly elementRef: ElementRef<unknown>,
    readonly renderer: Renderer2,
  ) {
    super(renderer, elementRef, false);
  }

  public ngOnInit() {
    this.percentageControl = new FormControl<PercentageType>(this.defaultValue);
    this.percentageControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (this.onChange) {
          this.onChange(value);
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  public override writeValue(value: PercentageType): void {
    if (isNaN(value as any)) {
      value = null;
    }

    if (value !== null) {
      value = this.transformValue(value);
    }

    if (this.percentageControl.value !== value) {
      this.percentageControl.setValue(value, { emitEvent: false });
      this.changeDetectorRef.detectChanges();
    }
  }

  public override setDisabledState(isDisabled: boolean): void {
    if (this.percentageControl.disabled === isDisabled) {
      return;
    }

    const method: keyof FormControl = isDisabled ? 'disable' : 'enable';
    this.percentageControl[method]({ emitEvent: false });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
  }

  protected onFocus(): void {
    this.inputControlElementRef.nativeElement.select();
  }

  protected clearValue(): void {
    this.percentageControl.setValue(null);
    this.clear.emit();
  }

  protected propagateMaxValueExceededEvent(event: MaxValueExceededEvent): void {
    this.maxExceeded.emit(event);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    const dot = '.';
    const isAllowedKey = [dot].includes(event.key);
    const isDot = event.key === dot;
    const controlValueHasADot =
      this.inputControlElementRef.nativeElement.value?.includes(dot) ?? false;
    const wouldBeSecondDot = isDot && controlValueHasADot;
    const isNotANumber = isNaN(event.key as unknown as number);
    if (event.key.length !== 1 || this.isCopyOrPaste(event)) {
      return;
    }

    if (isNotANumber && (!isAllowedKey || wouldBeSecondDot)) {
      event.preventDefault();
    }
  }

  private getFormat(): string {
    return this.format ?? this.defaultFormat;
  }

  private transformValue(value: string): string {
    const transformedValue = (
      this.decimalPipe.transform(value, this.getFormat()) ?? ''
    ).toString();

    return transformedValue;
  }

  private isCopyOrPaste(event: KeyboardEvent): boolean {
    return event.ctrlKey && (event.key === 'c' || event.key === 'v');
  }
}
